import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';

import Layout from './AppLayout';
import Header from '../../../shared/components/layout/Header';
import Button from '../../../shared/components/ui/Button';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import CompanyCard from '../ui/CompanyCard';
import MetricCard from '../ui/MetricCard';

import '../../styles/main.scss';
import Modal from '../../../shared/components/ui/Modal';
import AddCompany from '../forms/AddCompany';
import CompanyKybPanel from '../forms/CompanyKybPanel';
import DeleteConfirmationModal from '../ui/DeleteConfirmationModal';
import { useForm } from 'react-hook-form';
import {
  createCompany,
  deleteCompany,
  getAllCompanies,
  getAllProjects,
  handleCheckCompanyDelete
} from '../../services/api/implementation/impl';
import Loader from '../../../shared/components/layout/Loader';

import Cookies from 'js-cookie';
import NoDataFound from './NoDataFound';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import {
  BriefcaseBusiness,
  Building2,
  LayoutGrid,
  List,
  Plus,
  Search
} from 'lucide-react';
import deleteIcon from '../../../shared/assets/svg/delete.svg';
import { navigateWithReturn } from '../../../shared/navigation';

const CompaniesList = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();

  /* 
    Local state
  */
  const [openAddCompanyModal, setOpenAddCompanyModal] = useState(false);
  /** Company whose KYB checklist is open, or null. */
  const [kybCompanyId, setKybCompanyId] = useState(null);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [companiesToastShown, setCompaniesToastShown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tableDeletingId, setTableDeletingId] = useState(null);
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [companyTypeFilter, setCompanyTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const { collapsed } = useSidebar();
  /* 
    Data fetching
  */
  const token = Cookies.get('token');
  const {
    isLoading,
    isError,
    data: companies,
    refetch: refetchCompanies
  } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getAllCompanies(),
    enabled: !!token
  });

  const { data: allProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getAllProjects(),
    enabled: !!token
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Companies List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (token) {
      refetchCompanies();
    }
  }, [token, refetchCompanies]);

  // 1. Company List Viewed
  useEffect(() => {
    if (companies && companies.length > 0) {
      mixpanel.track('Company List Viewed', { company_count: companies.length });
    }
  }, [companies]);

  /* 
    Data mutation
  */
  const { mutate: mutateCompany, isLoading: isLoadingNewUserMutation } = useMutation({
    mutationFn: createCompany,
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (data) => {
      mixpanel.track('Company Created', { company_name: data?.company_name });
      toast.success('Company created successfully');
      reset();
      setOpenAddCompanyModal(false);
      setIsSubmitting(false);
      // Signatory, contacts and documents are keyed by company_id, so they can only be
      // collected once the company exists. Continue straight into the checklist.
      if (data?.id) setKybCompanyId(data.id);
      await refetchCompanies();
    },
    onError: (err) => {
      let errorMessage = 'An unexpected error occurred while creating the company.';
      setIsSubmitting(false);

      try {
        // Check if the error contains driverError details
        const detailMessage = err.response?.data?.message?.driverError?.detail;

        if (detailMessage && detailMessage.includes('Key (pan)=')) {
          // Extract PAN number using a regular expression
          const match = detailMessage.match(/Key \(pan\)=\((.*?)\)/);
          const panNumber = match?.[1]; // Extracted PAN number

          if (panNumber) {
            errorMessage = `PAN number ${panNumber} already exists.`;
          }
        } else if (err.message) {
          errorMessage = err.message; // Use general error message
        }
      } catch (error) {
        console.error('Error while parsing the error object:', error);
      }

      mixpanel.track('Company Creation Failed', { error: errorMessage });
      console.log('Final error message for toast:', errorMessage);
      toast.error(errorMessage);
    }
  });

  const { mutate: mutateTableDelete } = useMutation({
    mutationFn: (payload) => deleteCompany(payload),
    onSuccess: async () => {
      toast.success('Company deleted successfully');
      setTableDeletingId(null);
      setCanDelete(null);
      setSelectedIds([]);
      await refetchCompanies();
    },
    onError: (err) => {
      setTableDeletingId(null);
      toast.error(err?.response?.data?.message || err?.message || 'Error deleting company');
    }
  });

  const { mutate: mutateTableCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckCompanyDelete(id),
    onSuccess: (res, id) => {
      const company = (companies || []).find((item) => Number(item?.id) === Number(id));
      const companyName = company?.company_name || 'company';

      if (!res?.canDelete) {
        setResData({ ...res, name: companyName });
        setCanDelete('no');
        setTableDeletingId(null);
        return;
      }

      setResData({ name: companyName });
      setSelectedIds([Number(id)]);
      setCanDelete('yes');
      setTableDeletingId(null);
    },
    onError: (err) => {
      setTableDeletingId(null);
      toast.error(err?.response?.data?.message || err?.message || 'Error checking delete dependencies');
    }
  });

  /* 
    Event handlers
  */
  const handleToggleAddCompanyModal = useCallback(() => {
    setOpenAddCompanyModal((prev) => {
      const willOpen = !prev;
      if (willOpen) {
        mixpanel.track('Add Company Modal Opened');
      } else {
        mixpanel.track('Add Company Modal Closed');
      }
      return willOpen;
    });
  }, []);

  const handleOnBack = useCallback(() => {
    navigate('/builder/company');
  }, [navigate]);

  const handleDeleteFromModal = (ids) => {
    mutateTableDelete({ ids });
  };

  const handleRequestDelete = (id) => {
    if (!id) return;
    setTableDeletingId(id);
    mutateTableCheckDelete(id);
  };

  const { reset } = useForm();

  useEffect(() => {
    const source = Array.isArray(companies) ? companies : [];
    const normalizedQuery = searchQuery.trim().toLowerCase();

    const nextFiltered = source.filter((company) => {
      const companyName = String(company?.company_name || '').toLowerCase();
      const companyType = String(company?.company_type || '')
        .trim()
        .toLowerCase();

      const matchesSearch = !normalizedQuery || companyName.includes(normalizedQuery);
      const matchesType = companyTypeFilter === 'all' || companyType === companyTypeFilter;

      return matchesSearch && matchesType;
    });

    setFilteredCompanies(nextFiltered);
  }, [companies, searchQuery, companyTypeFilter]);

  useEffect(() => {
    if (openAddCompanyModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [openAddCompanyModal]);

  useEffect(() => {
    if (filteredCompanies?.length > 0 && !companiesToastShown) {
      // toast.success(`Successfully fetched ${filteredCompanies?.length} companies`);
      setCompaniesToastShown(true);
    }
  }, [filteredCompanies, companiesToastShown]);

  /* 
    Toasts
  */
  if (isLoading || isLoadingNewUserMutation) {
    return (
      <div className="h-screen w-screen">
        <Loader />
      </div>
    );
  }

  if (isError) {
    toast.error(`Error fetching companies`);
    navigate('/');
    return null;
  }

  const totalCompanies = companies?.length || 0;
  const totalProjects = allProjects?.length || 0;
  const companyTypeOptions = [
    'all',
    ...new Set(
      (companies || [])
        .map((company) => String(company?.company_type || '').trim().toLowerCase())
        .filter(Boolean)
    )
  ];

  // console.log(filteredCompanies?.length);

  /* 
    JSX
  */

  return (
    <>
      {openAddCompanyModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={56}
          name="Add Company"
          onClose={handleToggleAddCompanyModal}>
          <AddCompany
            onSubmit={mutateCompany}
            isSubmitting={isSubmitting}
            onClose={handleToggleAddCompanyModal}
          />
        </Modal>
      )}
      {kybCompanyId && (
        <Modal
          isDirty={false}
          confirmOnClose={false}
          width={56}
          name="Complete onboarding"
          onClose={() => setKybCompanyId(null)}>
          <CompanyKybPanel
            companyId={kybCompanyId}
            onClose={() => setKybCompanyId(null)}
          />
        </Modal>
      )}
      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${resData?.name || 'this company'}'`}
          onClose={() => {
            setCanDelete(null);
            setResData(null);
            setSelectedIds([]);
          }}
          width={45}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Looks like this company has dependencies, please delete following lands and projects
              to proceed
            </p>
            <div className="flex justify-start, align-middle flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {resData &&
                resData.reason?.map((r, index) => (
                  <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                    {index + 1}.{r}
                    {index < resData.reason.length - 1 ? ', ' : ''}
                  </span>
                ))}
            </div>
          </div>
        </Modal>
      )}
      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
            setResData(null);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData?.name}
            title={'company'}
            onClose={() => {
              setCanDelete(null);
              setSelectedIds([]);
              setResData(null);
            }}
            data={companies || []}
            type={'company'}
            setCanDelete={setCanDelete}
            onDeleteClick={handleDeleteFromModal}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}

      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header data={{ name: 'Company' }} onBack={handleOnBack} />
        </div>

        {companies && companies?.length > 0 ? (
          <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
            <GridWrapper width={100}>
              <div className="mt-[6rem]">
                <div className="mb-6 flex flex-wrap gap-3 sm:gap-4">
                  <MetricCard
                    title="Total Companies"
                    value={totalCompanies}
                    icon={<Building2 size={20} />}
                    iconBgClass="bg-[#EEF2FF]"
                    iconColorClass="text-[#4F46E5]"
                    valueColorClass="text-[#4F46E5]"
                    borderClass="border-[#DDE3F8]"
                    widthClass="w-[280px] max-w-full shrink-0"
                  />
                  <MetricCard
                    title="Total Projects"
                    value={totalProjects}
                    icon={<BriefcaseBusiness size={20} />}
                    iconBgClass="bg-[#E9F8F1]"
                    iconColorClass="text-[#0F766E]"
                    valueColorClass="text-[#0F766E]"
                    borderClass="border-[#CFEBDD]"
                    widthClass="w-[280px] max-w-full shrink-0"
                  />
                </div>

                <div className="mb-5 rounded-xl border border-[#E7EBF1] bg-white p-3">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-1 flex-col gap-3 sm:flex-row">
                      <div className="relative w-full sm:max-w-[280px]">
                        <Search
                          size={16}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]"
                        />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(event) => setSearchQuery(event.target.value)}
                          placeholder="Search company name"
                          className="w-full rounded-lg border border-[#D8DEE7] bg-[#F8FAFC] py-2 pl-9 pr-3 text-sm text-[#334155] outline-none focus:border-[#2A565E]"
                        />
                      </div>

                      <select
                        value={companyTypeFilter}
                        onChange={(event) => setCompanyTypeFilter(event.target.value)}
                        className="rounded-lg border border-[#D8DEE7] bg-[#F8FAFC] px-3 py-2 text-sm text-[#334155] outline-none focus:border-[#2A565E]">
                        {companyTypeOptions.map((type) => (
                          <option key={type} value={type}>
                            {type === 'all' ? 'Company Type' : type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="inline-flex items-center rounded-xl border border-[#DCE3EA] bg-white p-1 shadow-sm">
                      <button
                        type="button"
                        onClick={() => setViewMode('grid')}
                        className={`h-9 px-4 rounded-lg inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold transition-colors ${
                          viewMode === 'grid'
                            ? 'bg-[#EEF2F5] text-[#617087]'
                            : 'text-[#7A879B] hover:text-[#617087]'
                        }`}
                        aria-label="Card view">
                        <LayoutGrid size={14} />
                        <span>Card View</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setViewMode('table')}
                        className={`h-9 px-4 rounded-lg inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold transition-colors ${
                          viewMode === 'table'
                            ? 'bg-[#EEF2F5] text-[#0F4D52]'
                            : 'text-[#7A879B] hover:text-[#617087]'
                        }`}
                        aria-label="Table view">
                        <List size={14} />
                        <span>Table View</span>
                      </button>
                    </div>
                  </div>
                </div>

                {filteredCompanies?.length === 0 ? (
                  <div className="flex flex-col justify-center items-center w-full h-[300px] rounded-xl border border-dashed border-[#C7D3DE] bg-white">
                    <p className="text-lg font-semibold text-stone-500 mb-3">
                      No company matches current filters
                    </p>
                    <Button
                      displayType="primary"
                      onClick={handleToggleAddCompanyModal}
                      customStyle={{
                        borderRadius: '10px',
                        padding: '10px 16px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                      <Plus size={16} color="white" />
                      Add Company
                    </Button>
                  </div>
                ) : viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pb-4">
                    <button
                      type="button"
                      onClick={handleToggleAddCompanyModal}
                      className="w-full min-h-[205px] rounded-2xl border-2 border-dashed border-[#BFD4D8] bg-white text-[#2A565E] flex flex-col items-center justify-center gap-3 shadow-sm hover:bg-[#F7FBFC] hover:border-[#9EC5CC] transition-colors">
                      <span className="h-11 w-11 rounded-full bg-[#E8F3F5] flex items-center justify-center">
                        <Plus size={22} />
                      </span>
                      <span className="text-base font-semibold">Add Company</span>
                    </button>
                    {filteredCompanies?.map((item) => (
                      <CompanyCard
                        key={item?.id}
                        data={item}
                        onRequestDelete={handleRequestDelete}
                        isDeleteSubmitting={tableDeletingId === item?.id}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-[#E7EBF1] bg-white">
                    <table className="min-w-full text-sm">
                      <thead className="bg-[#F8FAFC] border-b border-[#E7EBF1] text-[#64748B]">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Company Name</th>
                          <th className="px-4 py-3 text-left font-semibold">Type</th>
                          <th className="px-4 py-3 text-left font-semibold">Location</th>
                          <th className="px-4 py-3 text-left font-semibold">GST</th>
                          <th className="px-4 py-3 text-left font-semibold">PAN</th>
                          <th className="px-4 py-3 text-left font-semibold">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-[#EEF2F6]">
                          <td colSpan={6} className="p-0">
                            <button
                              type="button"
                              onClick={handleToggleAddCompanyModal}
                              className="flex w-full items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-[#2A565E] transition-colors bg-[#FAFBFC] hover:bg-[#E8F3F5] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2A565E]">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-[#9EC5CC] bg-white">
                                <Plus size={18} strokeWidth={2.25} />
                              </span>
                              Add Company
                            </button>
                          </td>
                        </tr>
                        {filteredCompanies.map((item) => (
                          <tr key={item?.id} className="border-b border-[#EEF2F6] last:border-b-0">
                            <td className="px-4 py-3 text-[#0F172A] font-medium">
                              {item?.company_name || '--'}
                            </td>
                            <td className="px-4 py-3 text-[#334155]">{item?.company_type || '--'}</td>
                            <td className="px-4 py-3 text-[#334155]">
                              {[item?.city, item?.state].filter(Boolean).join(', ') || item?.address || '--'}
                            </td>
                            <td className="px-4 py-3 text-[#334155]">{item?.gst || '--'}</td>
                            <td className="px-4 py-3 text-[#334155]">{item?.pan || '--'}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigateWithReturn(
                                      navigate,
                                      `/builder/management/company/${item?.id}`,
                                      { returnTo: '/builder/management/company' }
                                    )
                                  }
                                  disabled={!item?.id}
                                  className="h-8 px-3 rounded-md border border-[#E7EBF1] bg-white text-xs font-semibold text-[#4D6C72] hover:bg-[#F8FAFC] disabled:opacity-50">
                                  View Company
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRequestDelete(item?.id)}
                                  disabled={!item?.id || tableDeletingId === item?.id}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#E7EBF1] bg-white hover:bg-[#F8FAFC] disabled:opacity-50">
                                  <img src={deleteIcon} alt="Delete company" className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </GridWrapper>
          </div>
        ) : (
          <NoDataFound onButtonClick={handleToggleAddCompanyModal} buttonText="Add Company" />
        )}
      </Layout>
    </>
  );
};

export default CompaniesList;
